import React from "react";
import { Modal, Row, Col, Form } from "react-bootstrap";
import Select from "react-select";

import ConditionalButton from "../../../../../layout/conditional-button";
import ConfirmModal from "../../../../../layout/modal";
import { isGuid } from "../../../../../utils/string";

const PRIORITY_OPTIONS = [
    { value: "", label: "Normal" },
    { value: "LOW", label: "Low" },
    { value: "HIGH", label: "High" },
    { value: "URGENT", label: "Urgent" },
];

const toDateTimeLocal = (value) => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
};

const fromDateTimeLocal = (value) => value ? new Date(value).toISOString() : null;

class BoardTaskModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            ...this.mapPropsToState(props),
            confirmDelete: {
                show: false
            }
        };
    }

    mapPropsToState = (props) => ({
        uuid: props.task?.uuid || null,
        personUuid: props.personUuid,
        personType: props.task?.personType || props.personType || "person",
        status: props.task?.status || props.status || props.columns?.[0]?.key || "NOT_STARTED",
        title: props.task?.title || "",
        description: props.task?.description || "",
        priority: props.task?.priority || "",
        assignedToUuid: props.task?.assignedToUuid || "",
        startBy: toDateTimeLocal(props.task?.startBy),
        finishBy: toDateTimeLocal(props.task?.finishBy),
    });

    componentDidUpdate(oldProps) {
        if (
            oldProps.show !== this.props.show ||
            oldProps.task?.uuid !== this.props.task?.uuid ||
            oldProps.task?.personType !== this.props.task?.personType ||
            oldProps.status !== this.props.status ||
            oldProps.personUuid !== this.props.personUuid ||
            oldProps.personType !== this.props.personType
        ) {
            this.setState(prev => ({
                ...prev,
                ...this.mapPropsToState(this.props),
                confirmDelete: {
                    show: false
                }
            }));
        }
    }

    onHideModal = (e) => {
        if (this.props.onCancel) this.props.onCancel(e);
    };

    onChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    };

    onStatusChange = (option) => {
        this.setState({ status: option?.value || this.props.columns?.[0]?.key || "NOT_STARTED" });
    };

    onPriorityChange = (option) => {
        this.setState({ priority: option?.value || "" });
    };

    onAssignedChange = (option) => {
        this.setState({ assignedToUuid: option?.value || "" });
    };

    onOk = async () => {
        if (!this.canSave) return;

        const result = await this.props.saveTask({
            uuid: this.state.uuid,
            personUuid: this.state.personUuid,
            personType: this.state.personType,
            status: this.state.status,
            title: String(this.state.title || "").trim(),
            description: this.state.description,
            priority: this.state.priority || null,
            assignedToUuid: this.state.assignedToUuid || null,
            startBy: fromDateTimeLocal(this.state.startBy),
            finishBy: fromDateTimeLocal(this.state.finishBy),
        });

        if (result?.success && this.props.onOk) this.props.onOk(result);
    };

    onDeleteClick = () => {
        if (!isGuid(this.state.uuid)) return;
        this.setState({
            confirmDelete: {
                show: true,
                title: "Delete Board Task",
                body: "Delete this board task? This cannot be undone."
            }
        });
    };

    onDeleteCancel = () => {
        this.setState({
            confirmDelete: {
                show: false
            }
        });
    };

    onDeleteConfirm = async () => {
        if (!isGuid(this.state.uuid) || !this.props.deleteTask) return;

        const result = await this.props.deleteTask({ uuid: this.state.uuid });
        if (result?.success && this.props.onDelete) this.props.onDelete(result);
    };

    get canSave() {
        return isGuid(this.state.personUuid) && Boolean(String(this.state.title || "").trim());
    }

    get canDelete() {
        return isGuid(this.state.uuid) && Boolean(this.props.deleteTask);
    }

    get statusOptions() {
        return (this.props.columns || []).map((column) => ({
            value: column.key,
            label: column.label
        }));
    }

    render() {
        const staffOptions = this.props.staffOptions || [];
        const statusOptions = this.statusOptions;

        return (
            <>
                <Modal size="lg" backdrop="static" keyboard show={this.props.show} onHide={this.onHideModal}>
                    <Modal.Header closeButton>
                        <Modal.Title id="staticBackdropLabel">Board Task</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3 pb-3 mt-1">
                            <Col md="8">
                                <div className="top-label">
                                    <Form.Control type="text" name="title" value={this.state.title} onChange={this.onChange} />
                                    <Form.Label>TITLE</Form.Label>
                                </div>
                            </Col>
                            <Col md="4">
                                <div className="top-label">
                                    <Select
                                        inputId="boardTaskStatus"
                                        classNamePrefix="react-select"
                                        options={statusOptions}
                                        value={statusOptions.find((option) => option.value === this.state.status) || statusOptions[0]}
                                        onChange={this.onStatusChange}
                                        isClearable={false}
                                    />
                                    <Form.Label>STATUS</Form.Label>
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="top-label">
                                    <Select
                                        inputId="boardTaskAssigned"
                                        classNamePrefix="react-select"
                                        options={staffOptions}
                                        value={staffOptions.find((option) => option.value === this.state.assignedToUuid) || null}
                                        onChange={this.onAssignedChange}
                                        isClearable
                                    />
                                    <Form.Label>ASSIGNED</Form.Label>
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="top-label">
                                    <Select
                                        inputId="boardTaskPriority"
                                        classNamePrefix="react-select"
                                        options={PRIORITY_OPTIONS}
                                        value={PRIORITY_OPTIONS.find((option) => option.value === this.state.priority) || PRIORITY_OPTIONS[0]}
                                        onChange={this.onPriorityChange}
                                        isClearable={false}
                                    />
                                    <Form.Label>PRIORITY</Form.Label>
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="top-label">
                                    <Form.Control type="datetime-local" name="startBy" value={this.state.startBy} onChange={this.onChange} />
                                    <Form.Label>START BY</Form.Label>
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="top-label">
                                    <Form.Control type="datetime-local" name="finishBy" value={this.state.finishBy} onChange={this.onChange} />
                                    <Form.Label>FINISH BY</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12">
                                <div className="top-label">
                                    <Form.Control as="textarea" rows={5} name="description" value={this.state.description || ""} onChange={this.onChange} />
                                    <Form.Label>DETAIL</Form.Label>
                                </div>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <ConditionalButton enabledVariant="outline-danger" display enabled={this.canDelete} onEnabledClick={this.onDeleteClick} icon="bin">delete</ConditionalButton>
                        <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onHideModal} icon="error-hexagon">cancel</ConditionalButton>
                        <ConditionalButton enabledVariant="outline-primary" display enabled={this.canSave} onEnabledClick={this.onOk} icon="save">ok</ConditionalButton>
                    </Modal.Footer>
                </Modal>
                <ConfirmModal
                    {...this.state.confirmDelete}
                    onCancel={this.onDeleteCancel}
                    buttons={[
                        {
                            caption: "delete",
                            variant: "danger",
                            display: true,
                            enabled: true,
                            onEnabledClick: this.onDeleteConfirm
                        },
                        {
                            default: true,
                            caption: "cancel",
                            variant: "primary",
                            display: true,
                            enabled: true,
                            onEnabledClick: this.onDeleteCancel
                        }
                    ]}
                />
            </>
        );
    }
}

export default BoardTaskModal;
